import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit2,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from "../../ui/inputFieldset";
import { SelectFieldset } from "../../ui/selectFieldset";
import { SelectTriggerUI } from "../../ui/selectTrigger";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { toast } from "sonner";
import Pagination from "../../ui/pagination";
import { FullScreenLoading } from "../../components/ui/loader";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { useScreenPermission } from "../../hooks/useScreenPermission";

interface SalaryHead {
  id: number;
  name: string;
  displayOrder: number;
  description: string;
  type: "Earning" | "Deduction" | "";
  formula: "Fixed Days" | "Percentage Of Basic" | "Percentage Of Earnings" | "";
  calculation: "Fixed" | "Percentage %" | "";
  value: string;
  expression: string;
  status: "Active" | "Inactive";
}

const initialSalaryHeads: SalaryHead[] = [
  {
    id: 1,
    name: "Basic Salary",
    displayOrder: 1,
    description: "Base monthly salary",
    type: "Earning",
    formula: "Fixed Days",
    calculation: "Fixed",
    value: "25000.00",
    expression: "",
    status: "Active",
  },
  {
    id: 2,
    name: "House Rent Allowance",
    displayOrder: 2,
    description: "40% of basic",
    type: "Earning",
    formula: "Percentage Of Basic",
    calculation: "Percentage %",
    value: "40.00",
    expression: "",
    status: "Active",
  },
  {
    id: 3,
    name: "Transport Allowance",
    displayOrder: 3,
    description: "Monthly transport",
    type: "Earning",
    formula: "Fixed Days",
    calculation: "Fixed",
    value: "3000.00",
    expression: "",
    status: "Active",
  },
  {
    id: 4,
    name: "Income Tax",
    displayOrder: 4,
    description: "Tax deduction",
    type: "Deduction",
    formula: "Percentage Of Earnings",
    calculation: "Percentage %",
    value: "10.00",
    expression: "",
    status: "Active",
  },
];

const emptyForm: Omit<SalaryHead, "id"> = {
  name: "",
  displayOrder: 1,
  description: "",
  type: "",
  formula: "",
  calculation: "",
  value: "",
  expression: "",
  status: "Active",
};

interface SalaryHeadErrors {
  name: string;
  displayOrder: string;
  description: string;
  type: string;
  formula: string;
  calculation: string;
  value: string;
  expression: string;
}

const SALARY_HEAD_NAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9 &()./-]{0,49}$/;
const DESCRIPTION_MAX_LENGTH = 200;
const EXPRESSION_MAX_LENGTH = 500;

const createEmptyErrors = (): SalaryHeadErrors => ({
  name: "",
  displayOrder: "",
  description: "",
  type: "",
  formula: "",
  calculation: "",
  value: "",
  expression: "",
});

const validateSalaryHead = (
  form: Omit<SalaryHead, "id">,
  salaryHeads: SalaryHead[],
  editingId?: number,
): SalaryHeadErrors => {
  const errors = createEmptyErrors();
  const name = form.name.trim();
  if (!name) {
    errors.name = "Head Name is required";
  } else if (name.length > 50) {
    errors.name = "Head Name cannot exceed 50 characters";
  } else if (!SALARY_HEAD_NAME_REGEX.test(name)) {
    errors.name =
      "Head Name can contain only letters, numbers, spaces and . & ( ) / -";
  }
  const duplicateName = salaryHeads.some(
    (item) =>
      item.id !== editingId &&
      item.name.trim().toLowerCase() === name.toLowerCase(),
  );
  if (name && duplicateName) {
    errors.name = "Salary Head Name already exists.";
  }
  const displayOrder = String(form.displayOrder).trim();
  if (!displayOrder) {
    errors.displayOrder = "Display Order is required";
  } else if (!/^[1-9]\d*$/.test(displayOrder)) {
    errors.displayOrder = "Display Order must be a positive whole number.";
  }
  if (!form.description.trim()) {
    errors.description = "Description is required";
  } else if (form.description.trim().length > DESCRIPTION_MAX_LENGTH) {
    errors.description = `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters.`;
  }
  if (!form.type) {
    errors.type = "Head Type is required";
  }
  if (!form.calculation) {
    errors.calculation = "Calculation Type is required";
  }
  if (!form.formula) {
    errors.formula = "Formula Type is required";
  }
  const value = form.value.trim();
  if (!value) {
    errors.value = "Formula Value is required";
  } else if (!/^\d+(\.\d{1,2})?$/.test(value)) {
    errors.value =
      "Formula Value must be a valid number with maximum 2 decimal places.";
  } else {
    const numericValue = Number(value);
    if (numericValue < 0) {
      errors.value = "Formula Value cannot be negative.";
    }
    if (form.calculation === "Percentage %" && numericValue > 100) {
      errors.value = "Percentage value cannot be greater than 100.";
    }
  }
  if (form.expression.trim().length > EXPRESSION_MAX_LENGTH) {
    errors.expression = `Formula Expression cannot exceed ${EXPRESSION_MAX_LENGTH} characters.`;
  }
  return errors;
};

export function SalaryHeadsPage() {
  const { setPageHeader } = usePageHeader();
  const {
    canAdd,
    canEdit,
    canDelete,
    screenName,
    icon: ScreenIcon,
  } = useScreenPermission();
  const [salaryHeads, setSalaryHeads] =
    useState<SalaryHead[]>(initialSalaryHeads);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SalaryHead | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<SalaryHead | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState<Omit<SalaryHead, "id">>(emptyForm);
  const [editFormData, setEditFormData] =
    useState<Omit<SalaryHead, "id">>(emptyForm);
  const [errors, setErrors] = useState<SalaryHeadErrors>(createEmptyErrors());
  const [editErrors, setEditErrors] =
    useState<SalaryHeadErrors>(createEmptyErrors());

  useEffect(() => {
    setPageHeader({
      title: screenName || "Salary",
      icon: ScreenIcon ? (
        <ScreenIcon className="h-6 w-6" />
      ) : (
        <Users className="h-6 w-6" />
      ),
    });
    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => {
      setSalaryHeads(initialSalaryHeads);
      setLoading(false);
    }, 200);
    return () => window.clearTimeout(timer);
  }, []);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    const filtered = salaryHeads.filter((item) => {
      const search = searchTerm.trim().toLowerCase();
      if (!search) {
        return true;
      }
      return (
        item.name.toLowerCase().includes(search) ||
        item.description.toLowerCase().includes(search) ||
        item.type.toLowerCase().includes(search) ||
        item.formula.toLowerCase().includes(search) ||
        item.calculation.toLowerCase().includes(search) ||
        String(item.displayOrder).includes(search) ||
        item.value.toLowerCase().includes(search)
      );
    });
    return [...filtered].sort((a, b) => {
      if (!sortColumn) {
        return 0;
      }
      let aValue: string | number = a[sortColumn as keyof SalaryHead] as
        | string
        | number;
      let bValue: string | number = b[sortColumn as keyof SalaryHead] as
        | string
        | number;
      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (aValue < bValue) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [salaryHeads, searchTerm, sortColumn, sortDirection]);

  const pageRows = sortedData.slice((pageNo - 1) * pageSize, pageNo * pageSize);

  const resetForm = () => {
    setFormData({
      ...emptyForm,
    });
    setErrors(createEmptyErrors());
  };

  const resetEditForm = () => {
    setEditFormData({
      ...emptyForm,
    });
    setEditErrors(createEmptyErrors());
    setEditingItem(null);
  };

  const openAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const handleEdit = (item: SalaryHead) => {
    setEditingItem(item);
    setEditFormData({
      name: item.name,
      displayOrder: item.displayOrder,
      description: item.description,
      type: item.type,
      formula: item.formula,
      calculation: item.calculation,
      value: item.value,
      expression: item.expression,
      status: item.status,
    });
    setEditErrors(createEmptyErrors());
    setIsEditDialogOpen(true);
  };

  const handleAdd = async () => {
    const newErrors = validateSalaryHead(formData, salaryHeads);
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some(Boolean);
    if (hasErrors) {
      return;
    }
    try {
      setSaving(true);
      const newId =
        salaryHeads.length > 0
          ? Math.max(...salaryHeads.map((item) => item.id)) + 1
          : 1;
      const newSalaryHead: SalaryHead = {
        id: newId,
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        value: formData.value.trim(),
        expression: formData.expression.trim(),
      };
      setSalaryHeads((current) => [newSalaryHead, ...current]);
      toast.success("Salary Head added successfully.");
      setIsAddDialogOpen(false);
      resetForm();
      setPageNo(1);
    } catch (error) {
      console.error("Failed to add salary head:", error);
      toast.error("Failed to add salary head. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingItem) {
      return;
    }
    const newErrors = validateSalaryHead(
      editFormData,
      salaryHeads,
      editingItem.id,
    );
    setEditErrors(newErrors);
    const hasErrors = Object.values(newErrors).some(Boolean);
    if (hasErrors) {
      return;
    }
    try {
      setSaving(true);
      setSalaryHeads((current) =>
        current.map((item) =>
          item.id === editingItem.id
            ? {
                id: editingItem.id,
                ...editFormData,
                name: editFormData.name.trim(),
                description: editFormData.description.trim(),
                value: editFormData.value.trim(),
                expression: editFormData.expression.trim(),
              }
            : item,
        ),
      );
      toast.success("Salary Head updated successfully.");
      setIsEditDialogOpen(false);
      resetEditForm();
    } catch (error) {
      console.error("Failed to update salary head:", error);
      toast.error("Failed to update salary head. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: SalaryHead) => {
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) {
      return;
    }
    try {
      setDeleting(true);
      setSalaryHeads((current) =>
        current.filter((item) => item.id !== deletingItem.id),
      );
      toast.success("Salary Head deleted successfully.");
      setIsDeleteDialogOpen(false);
      setDeletingItem(null);
      if (pageNo > 1 && pageRows.length === 1) {
        setPageNo((current) => Math.max(1, current - 1));
      }
    } catch (error) {
      console.error("Failed to delete salary head:", error);
      toast.error("Failed to delete salary head. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {(loading || saving || deleting) &&
        createPortal(
          <div className="fixed inset-0 z-[100]">
            <FullScreenLoading
              title={
                deleting
                  ? "Deleting salary head..."
                  : saving
                    ? "Saving salary head..."
                    : "Loading salary heads..."
              }
            />
          </div>,
          document.body,
        )}

      <div className="space-y-6 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by head name, type or formula..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setPageNo(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-2 shrink-0">
            {canAdd && (
              <Button
                onClick={openAddDialog}
                style={{
                  background: "#5B9BD5",
                }}
                className="text-white"
                disabled={saving || deleting}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Salary Head
              </Button>
            )}
          </div>
        </div>

        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{
            marginTop: "10px",
            gap: "0px",
          }}
        >
          <CardContent
            style={{
              padding: "0px 0px 10px 0px",
            }}
          >
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow
                    style={{
                      backgroundColor: "#E3F2FD",
                    }}
                  >
                    {/* Head Name */}
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Head Name
                        {sortColumn === "name" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    {/* Order */}
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("displayOrder")}
                    >
                      <div className="flex items-center gap-1">
                        Order
                        {sortColumn === "displayOrder" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    {/* Description */}
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("description")}
                    >
                      <div className="flex items-center gap-1">
                        Description
                        {sortColumn === "description" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    {/* Type */}
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("type")}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortColumn === "type" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    {/* Formula Type */}
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("formula")}
                    >
                      <div className="flex items-center gap-1">
                        Formula Type
                        {sortColumn === "formula" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    {/* Calculation */}
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("calculation")}
                    >
                      <div className="flex items-center gap-1">
                        Calculation
                        {sortColumn === "calculation" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    {/* Value */}
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("value")}
                    >
                      <div className="flex items-center gap-1">
                        Value
                        {sortColumn === "value" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    {/* Status */}
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs text-center"
                    >
                      Status
                    </TableHead>

                    {/* Actions */}
                    {(canEdit || canDelete) && (
                      <TableHead
                        style={{
                          color: "#5B9BD5",
                        }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={canEdit || canDelete ? 9 : 8}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No salary heads found
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((item) => (
                      <TableRow key={item.id} className="hover:bg-sky-50/30">
                        {/* Head Name */}
                        <TableCell className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {item.name}
                            </span>
                          </div>
                        </TableCell>

                        {/* Order */}
                        <TableCell className="px-4 py-2">
                          <span className="text-xs text-gray-600">
                            {item.displayOrder}
                          </span>
                        </TableCell>

                        {/* Description */}
                        <TableCell className="px-4 py-2">
                          <span className="text-xs text-gray-600">
                            {item.description}
                          </span>
                        </TableCell>

                        {/* Type */}
                        <TableCell className="text-xs px-4 py-2">
                          <Badge
                            variant="outline"
                            className={
                              item.type === "Earning"
                                ? "bg-green-50 text-green-700 border-green-200 text-xs"
                                : "bg-red-50 text-red-700 border-red-200 text-xs"
                            }
                          >
                            {item.type}
                          </Badge>
                        </TableCell>

                        {/* Formula */}
                        <TableCell className="px-4 py-2">
                          <span className="text-xs text-gray-600">
                            {item.formula}
                          </span>
                        </TableCell>

                        {/* Calculation */}
                        <TableCell className="px-4 py-2">
                          <span className="text-xs text-gray-600">
                            {item.calculation}
                          </span>
                        </TableCell>

                        {/* Value */}
                        <TableCell className="px-4 py-2">
                          <span className="text-xs text-gray-700 font-medium">
                            {item.value}
                          </span>
                        </TableCell>

                        {/* Status */}
                        <TableCell className="text-xs px-4 py-2 text-center">
                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "Active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status}
                          </Badge>
                        </TableCell>

                        {/* Actions */}
                        {(canEdit || canDelete) && (
                          <TableCell className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEdit(item)}
                                  className="text-[#5B9BD5] hover:bg-sky-50 h-5 w-5 p-0"
                                  title="Edit"
                                  disabled={saving || deleting}
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </Button>
                              )}

                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(item)}
                                  className="text-red-500 hover:bg-red-50 h-5 w-5 p-0"
                                  title="Delete"
                                  disabled={saving || deleting}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="lg:hidden space-y-3 p-4">
              {sortedData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No salary heads found</p>
                </div>
              ) : (
                pageRows.map((item) => (
                  <Card
                    key={item.id}
                    className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                  >
                    <CardContent
                      style={{
                        padding: "14px",
                      }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {item.name}
                          </p>

                          <Badge
                            variant="outline"
                            className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-xs mt-1"
                          >
                            Order {item.displayOrder}
                          </Badge>
                        </div>

                        <Badge
                          variant="outline"
                          className={
                            item.type === "Earning"
                              ? "bg-green-50 text-green-700 border-green-200 text-xs"
                              : "bg-red-50 text-red-700 border-red-200 text-xs"
                          }
                        >
                          {item.type}
                        </Badge>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex items-start gap-2">
                          <span className="text-xs text-gray-500">
                            Description:
                          </span>

                          <span className="text-sm text-gray-700">
                            {item.description}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            Formula:
                          </span>

                          <span className="text-sm text-gray-700">
                            {item.formula}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            Calculation:
                          </span>

                          <span className="text-sm text-gray-700">
                            {item.calculation}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Value:</span>

                          <span className="text-sm font-medium text-gray-700">
                            {item.value}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Status:</span>

                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "Active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(item)}
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              disabled={saving || deleting}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          )}

                          {canDelete && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(item)}
                              className="h-8 text-xs text-red-600 hover:bg-red-50"
                              disabled={saving || deleting}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>

          <Pagination
            totalItems={sortedData.length}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="salary heads"
          />
        </Card>

        <Dialog
          open={isAddDialogOpen}
          onOpenChange={(open) => {
            if (!saving) {
              setIsAddDialogOpen(open);

              if (!open) {
                resetForm();
              }
            }
          }}
        >
          <DialogContent className="sm:max-w-[700px] bg-white">
            <DialogHeader>
              <DialogTitle
                style={{
                  color: "#5B9BD5",
                }}
              >
                Add Salary Head
              </DialogTitle>

              <DialogDescription>
                Create a new salary head entry
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Head Name */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Head Name"
                    required
                    disabled={saving}
                    id="salary-head-name"
                    value={formData.name}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 50);

                      setFormData({
                        ...formData,
                        name: value,
                      });

                      setErrors({
                        ...errors,
                        name: "",
                      });
                    }}
                    placeholder="e.g. Basic Salary"
                    className={`text-sm ${
                      errors.name ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.name && (
                    <p className="text-red-500 text-xs">{errors.name}</p>
                  )}
                </div>

                {/* Display Order */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Display Order"
                    required
                    disabled={saving}
                    id="salary-head-order"
                    type="number"
                    min="1"
                    value={String(formData.displayOrder)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");

                      setFormData({
                        ...formData,
                        displayOrder: value === "" ? 0 : Number(value),
                      });

                      setErrors({
                        ...errors,
                        displayOrder: "",
                      });
                    }}
                    placeholder="e.g. 1"
                    className={`text-sm ${
                      errors.displayOrder
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {errors.displayOrder && (
                    <p className="text-red-500 text-xs">
                      {errors.displayOrder}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Head Type */}
                <div className="space-y-2">
                  <SelectFieldset label="Head Type" required>
                    <Select
                      value={formData.type}
                      disabled={saving}
                      onValueChange={(value: "Earning" | "Deduction") => {
                        setFormData({
                          ...formData,
                          type: value,
                        });

                        setErrors({
                          ...errors,
                          type: "",
                        });
                      }}
                    >
                      <SelectTriggerUI
                        className={`h-9 text-sm ${
                          errors.type ? "border-red-500 focus:ring-red-500" : ""
                        }`}
                      >
                        <SelectValue placeholder="Choose Head Type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        <SelectItem value="Earning">Earning</SelectItem>

                        <SelectItem value="Deduction">Deduction</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  {errors.type && (
                    <p className="text-red-500 text-xs">{errors.type}</p>
                  )}
                </div>

                {/* Calculation */}
                <div className="space-y-2">
                  <SelectFieldset label="Calculation Type" required>
                    <Select
                      value={formData.calculation}
                      disabled={saving}
                      onValueChange={(value: "Fixed" | "Percentage %") => {
                        setFormData({
                          ...formData,
                          calculation: value,
                        });

                        setErrors({
                          ...errors,
                          calculation: "",
                          value: "",
                        });
                      }}
                    >
                      <SelectTriggerUI
                        className={`h-9 text-sm ${
                          errors.calculation
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`}
                      >
                        <SelectValue placeholder="Choose Calculation Type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        <SelectItem value="Fixed">Fixed</SelectItem>

                        <SelectItem value="Percentage %">
                          Percentage %
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  {errors.calculation && (
                    <p className="text-red-500 text-xs">{errors.calculation}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Formula Type */}
                <div className="space-y-2">
                  <SelectFieldset label="Formula Type" required>
                    <Select
                      value={formData.formula}
                      disabled={saving}
                      onValueChange={(
                        value:
                          | "Fixed Days"
                          | "Percentage Of Basic"
                          | "Percentage Of Earnings",
                      ) => {
                        setFormData({
                          ...formData,
                          formula: value,
                        });

                        setErrors({
                          ...errors,
                          formula: "",
                        });
                      }}
                    >
                      <SelectTriggerUI
                        className={`h-9 text-sm ${
                          errors.formula
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`}
                      >
                        <SelectValue placeholder="Choose Formula Type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        <SelectItem value="Fixed Days">Fixed Days</SelectItem>

                        <SelectItem value="Percentage Of Basic">
                          Percentage Of Basic
                        </SelectItem>

                        <SelectItem value="Percentage Of Earnings">
                          Percentage Of Earnings
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  {errors.formula && (
                    <p className="text-red-500 text-xs">{errors.formula}</p>
                  )}
                </div>

                {/* Formula Value */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Formula Value"
                    required
                    disabled={saving}
                    id="salary-head-value"
                    type="number"
                    step="0.01"
                    min="0"
                    max={
                      formData.calculation === "Percentage %"
                        ? "100"
                        : undefined
                    }
                    value={formData.value}
                    onChange={(e) => {
                      const value = e.target.value;

                      setFormData({
                        ...formData,
                        value,
                      });

                      setErrors({
                        ...errors,
                        value: "",
                      });
                    }}
                    placeholder="0.00"
                    className={`text-sm ${
                      errors.value ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.value && (
                    <p className="text-red-500 text-xs">{errors.value}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Formula Expression"
                  disabled={saving}
                  id="salary-head-expression"
                  value={formData.expression}
                  onChange={(e) => {
                    const value = e.target.value.slice(
                      0,
                      EXPRESSION_MAX_LENGTH,
                    );

                    setFormData({
                      ...formData,
                      expression: value,
                    });

                    setErrors({
                      ...errors,
                      expression: "",
                    });
                  }}
                  placeholder="e.g. (Basic * 0.5) + Allowance"
                  className={`text-sm ${
                    errors.expression ? "border-red-500 focus:ring-red-500" : ""
                  }`}
                />

                {errors.expression && (
                  <p className="text-red-500 text-xs">{errors.expression}</p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Description"
                  required
                  disabled={saving}
                  id="salary-head-description"
                  value={formData.description}
                  onChange={(e) => {
                    const value = e.target.value.slice(
                      0,
                      DESCRIPTION_MAX_LENGTH,
                    );

                    setFormData({
                      ...formData,
                      description: value,
                    });

                    setErrors({
                      ...errors,
                      description: "",
                    });
                  }}
                  placeholder="Add a description"
                  className={`text-sm ${
                    errors.description
                      ? "border-red-500 focus:ring-red-500"
                      : ""
                  }`}
                />

                {errors.description && (
                  <p className="text-red-500 text-xs">{errors.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={formData.status}
                    disabled={saving}
                    onValueChange={(value: "Active" | "Inactive") =>
                      setFormData({
                        ...formData,
                        status: value,
                      })
                    }
                  >
                    <SelectTriggerUI className="h-9 text-sm">
                      <SelectValue />
                    </SelectTriggerUI>

                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>

                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={handleAdd}
                disabled={saving}
                style={{
                  background: "#5B9BD5",
                }}
                className="text-white"
              >
                Add Salary Head
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            if (!saving) {
              setIsEditDialogOpen(open);

              if (!open) {
                resetEditForm();
              }
            }
          }}
        >
          <DialogContent className="sm:max-w-[700px] bg-white">
            <DialogHeader>
              <DialogTitle
                style={{
                  color: "#5B9BD5",
                }}
              >
                Edit Salary Head
              </DialogTitle>

              <DialogDescription>
                Update salary head information
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Head Name + Order */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Head Name"
                    required
                    id="edit-salary-head-name"
                    value={editFormData.name}
                    disabled={saving}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 50);

                      setEditFormData({
                        ...editFormData,
                        name: value,
                      });

                      setEditErrors({
                        ...editErrors,
                        name: "",
                      });
                    }}
                    placeholder="e.g. Basic Salary"
                    className={`text-sm ${
                      editErrors.name ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {editErrors.name && (
                    <p className="text-red-500 text-xs">{editErrors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Display Order"
                    required
                    id="edit-salary-head-order"
                    type="number"
                    min="1"
                    value={String(editFormData.displayOrder)}
                    disabled={saving}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");

                      setEditFormData({
                        ...editFormData,
                        displayOrder: value === "" ? 0 : Number(value),
                      });

                      setEditErrors({
                        ...editErrors,
                        displayOrder: "",
                      });
                    }}
                    placeholder="e.g. 1"
                    className={`text-sm ${
                      editErrors.displayOrder
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {editErrors.displayOrder && (
                    <p className="text-red-500 text-xs">
                      {editErrors.displayOrder}
                    </p>
                  )}
                </div>
              </div>

              {/* Head Type + Calculation */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <SelectFieldset label="Head Type" required>
                    <Select
                      value={editFormData.type}
                      disabled={saving}
                      onValueChange={(value: "Earning" | "Deduction") => {
                        setEditFormData({
                          ...editFormData,
                          type: value,
                        });

                        setEditErrors({
                          ...editErrors,
                          type: "",
                        });
                      }}
                    >
                      <SelectTriggerUI
                        className={`h-9 text-sm ${
                          editErrors.type
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`}
                      >
                        <SelectValue placeholder="Choose Head Type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        <SelectItem value="Earning">Earning</SelectItem>

                        <SelectItem value="Deduction">Deduction</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  {editErrors.type && (
                    <p className="text-red-500 text-xs">{editErrors.type}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <SelectFieldset label="Calculation Type" required>
                    <Select
                      value={editFormData.calculation}
                      disabled={saving}
                      onValueChange={(value: "Fixed" | "Percentage %") => {
                        setEditFormData({
                          ...editFormData,
                          calculation: value,
                        });

                        setEditErrors({
                          ...editErrors,
                          calculation: "",
                          value: "",
                        });
                      }}
                    >
                      <SelectTriggerUI
                        className={`h-9 text-sm ${
                          editErrors.calculation
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`}
                      >
                        <SelectValue placeholder="Choose Calculation Type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        <SelectItem value="Fixed">Fixed</SelectItem>

                        <SelectItem value="Percentage %">
                          Percentage %
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  {editErrors.calculation && (
                    <p className="text-red-500 text-xs">
                      {editErrors.calculation}
                    </p>
                  )}
                </div>
              </div>

              {/* Formula + Value */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <SelectFieldset label="Formula Type" required>
                    <Select
                      value={editFormData.formula}
                      disabled={saving}
                      onValueChange={(
                        value:
                          | "Fixed Days"
                          | "Percentage Of Basic"
                          | "Percentage Of Earnings",
                      ) => {
                        setEditFormData({
                          ...editFormData,
                          formula: value,
                        });

                        setEditErrors({
                          ...editErrors,
                          formula: "",
                        });
                      }}
                    >
                      <SelectTriggerUI
                        className={`h-9 text-sm ${
                          editErrors.formula
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`}
                      >
                        <SelectValue placeholder="Choose Formula Type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        <SelectItem value="Fixed Days">Fixed Days</SelectItem>

                        <SelectItem value="Percentage Of Basic">
                          Percentage Of Basic
                        </SelectItem>

                        <SelectItem value="Percentage Of Earnings">
                          Percentage Of Earnings
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  {editErrors.formula && (
                    <p className="text-red-500 text-xs">{editErrors.formula}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Formula Value"
                    required
                    id="edit-salary-head-value"
                    type="number"
                    step="0.01"
                    min="0"
                    max={
                      editFormData.calculation === "Percentage %"
                        ? "100"
                        : undefined
                    }
                    value={editFormData.value}
                    disabled={saving}
                    onChange={(e) => {
                      setEditFormData({
                        ...editFormData,
                        value: e.target.value,
                      });

                      setEditErrors({
                        ...editErrors,
                        value: "",
                      });
                    }}
                    placeholder="0.00"
                    className={`text-sm ${
                      editErrors.value
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {editErrors.value && (
                    <p className="text-red-500 text-xs">{editErrors.value}</p>
                  )}
                </div>
              </div>

              {/* Expression */}
              <div className="space-y-2">
                <InputFieldset
                  label="Formula Expression"
                  id="edit-salary-head-expression"
                  value={editFormData.expression}
                  disabled={saving}
                  onChange={(e) => {
                    setEditFormData({
                      ...editFormData,
                      expression: e.target.value.slice(
                        0,
                        EXPRESSION_MAX_LENGTH,
                      ),
                    });

                    setEditErrors({
                      ...editErrors,
                      expression: "",
                    });
                  }}
                  placeholder="e.g. (Basic * 0.5) + Allowance"
                  className={`text-sm ${
                    editErrors.expression
                      ? "border-red-500 focus:ring-red-500"
                      : ""
                  }`}
                />

                {editErrors.expression && (
                  <p className="text-red-500 text-xs">
                    {editErrors.expression}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <InputFieldset
                  label="Description"
                  required
                  id="edit-salary-head-description"
                  value={editFormData.description}
                  disabled={saving}
                  onChange={(e) => {
                    setEditFormData({
                      ...editFormData,
                      description: e.target.value.slice(
                        0,
                        DESCRIPTION_MAX_LENGTH,
                      ),
                    });

                    setEditErrors({
                      ...editErrors,
                      description: "",
                    });
                  }}
                  placeholder="Add a description"
                  className={`text-sm ${
                    editErrors.description
                      ? "border-red-500 focus:ring-red-500"
                      : ""
                  }`}
                />

                {editErrors.description && (
                  <p className="text-red-500 text-xs">
                    {editErrors.description}
                  </p>
                )}
              </div>

              {/* Status */}
              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={editFormData.status}
                    disabled={saving}
                    onValueChange={(value: "Active" | "Inactive") =>
                      setEditFormData({
                        ...editFormData,
                        status: value,
                      })
                    }
                  >
                    <SelectTriggerUI className="h-9 text-sm">
                      <SelectValue />
                    </SelectTriggerUI>

                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>

                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setIsEditDialogOpen(false);
                  resetEditForm();
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={handleUpdate}
                disabled={saving}
                style={{
                  background: "#5B9BD5",
                }}
                className="text-white"
              >
                Update Salary Head
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            if (!deleting) {
              setIsDeleteDialogOpen(open);

              if (!open) {
                setDeletingItem(null);
              }
            }
          }}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Salary Head
              </DialogTitle>

              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">"{deletingItem?.name}"</span>?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                disabled={deleting}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
